/**
 * Full-catalog sibling color swatch hydration - Vastradevi Theme
 *
 * Collection cards can only scan the first ~50 products for siblings
 * (Liquid caps un-paginated collections.all.products, and the grid already
 * uses one paginate tag). This pulls a lightweight JSON feed of every
 * product's `sibling_group` metafield from a dedicated, paginated template
 * (/collections/all?view=siblings) - which CAN read the metafield and page
 * through all 1000+ products - and fills in every colour on the cards.
 */
(function () {
  'use strict';

  var FEED_URL = '/collections/all?view=siblings';
  var MAX_PAGES = 40; // safety cap: 40 * 250 = 10,000 products
  var indexPromise = null;

  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    var amount = (cents / 100).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return '₹' + amount;
  }

  function escapeHTML(str) {
    return String(str).replace(/[&<>'"]/g,
      function (tag) { return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' })[tag] || tag; }
    );
  }

  function fetchPageProducts(page) {
    // All products are synced server-side via product.metafields.custom.sibling_list.
    // Client-side feed fetch is disabled for 100% server-side rendering & zero network calls.
    return Promise.resolve({ products: [] });
  }

  // Fetch page 1 (learn the page count), then fetch the rest IN PARALLEL.
  function fetchAllFeed() {
    return fetchPageProducts(1).then(function (first) {
      var products = first.products || [];
      var pages = Math.min(first.pages || 1, MAX_PAGES);
      if (pages <= 1) return products;

      var requests = [];
      for (var p = 2; p <= pages; p++) {
        requests.push(fetchPageProducts(p));
      }
      return Promise.all(requests).then(function (rest) {
        rest.forEach(function (d) { products = products.concat(d.products || []); });
        return products;
      });
    });
  }

  function mapFromProducts(products) {
    var map = {};
    products.forEach(function (p) {
      var sg = p.sg;
      if (!sg) return;
      if (!map[sg]) map[sg] = [];
      map[sg].push({
        handle: p.h,
        title: p.t,
        colorName: p.c || p.t,
        url: p.u,
        image: p.i || '',
        hoverImage: p.i2 || p.i || '',
        // Feed prices are in cents (Liquid product.price); keep as cents.
        price: typeof p.p === 'number' ? p.p : null,
        compareAtPrice: (typeof p.cp === 'number' && p.cp > 0) ? p.cp : null
      });
    });
    return map;
  }

  function buildIndex() {
    if (indexPromise) return indexPromise;
    indexPromise = fetchAllFeed()
      .then(function (products) { return mapFromProducts(products); })
      .catch(function () { return {}; });
    return indexPromise;
  }

  function buildSwatch(entry, isActive) {
    // Prices come from the feed already in cents (Liquid product.price).
    // data-price/data-compare carry raw cents (not pre-formatted) so a
    // later currency switch can reformat them without rebuilding the swatch.
    var discount = '';
    if (entry.compareAtPrice && entry.price && entry.compareAtPrice > entry.price) {
      discount = Math.round(((entry.compareAtPrice - entry.price) * 100) / entry.compareAtPrice);
    }
    var shortTitle = escapeHTML(entry.colorName || entry.title);

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'col-card__img-swatch' + (isActive ? ' active' : '');
    btn.setAttribute('data-image', entry.image);
    btn.setAttribute('data-hover-image', entry.hoverImage);
    btn.setAttribute('data-url', entry.url);
    btn.setAttribute('data-handle', entry.handle);
    btn.setAttribute('data-title', entry.title);
    btn.setAttribute('data-price', entry.price != null ? entry.price : '');
    btn.setAttribute('data-compare', discount !== '' ? entry.compareAtPrice : '');
    btn.setAttribute('data-discount', discount);
    btn.setAttribute('aria-label', entry.title);
    btn.innerHTML =
      '<img src="' + entry.image + '" alt="' + shortTitle + '" loading="lazy" width="44" height="54">' +
      '<span class="col-card__swatch-tip">' + shortTitle + '</span>';
    return btn;
  }

  // ── Left/right arrows on the colour swatch row. Wired independently of
  // wireCardSwatches (own guard flag) so it works whether swatches are
  // present at wiring time (server-rendered) or added later (JS hydration/
  // filtered-card rebuild) - a ResizeObserver + MutationObserver on the
  // track keep the overflow/disabled state correct as content changes. ──
  function wireSwatchNav(card) {
    var wrap = card.querySelector('.col-card__swatches-wrap');
    if (!wrap || wrap.dataset.vdNavWired) return;
    wrap.dataset.vdNavWired = '1';

    var track = wrap.querySelector('.col-card__swatches--img');
    var prevBtn = wrap.querySelector('.col-card__swatch-nav--prev');
    var nextBtn = wrap.querySelector('.col-card__swatch-nav--next');
    if (!track || !prevBtn || !nextBtn) return;

    function updateState() {
      var hasOverflow = track.scrollWidth > track.clientWidth + 2;
      wrap.classList.toggle('has-overflow', hasOverflow);
      prevBtn.disabled = track.scrollLeft <= 0;
      nextBtn.disabled = track.scrollLeft >= track.scrollWidth - track.clientWidth - 1;
    }

    prevBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      track.scrollBy({ left: -100, behavior: 'smooth' });
    });
    nextBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      track.scrollBy({ left: 100, behavior: 'smooth' });
    });
    track.addEventListener('scroll', updateState, { passive: true });

    if (window.ResizeObserver) {
      new ResizeObserver(updateState).observe(track);
    }
    if (window.MutationObserver) {
      new MutationObserver(updateState).observe(track, { childList: true });
    }

    updateState();
  }

  // ── Sibling image swatch: hover = preview, click = update card in place (no
  // redirect). Single canonical wiring for every .col-card on the site
  // (collection grids, search, featured-products, related products,
  // wishlist) — guarded so re-running it (e.g. after hydration) is a no-op. ──
  function wireCardSwatches(card) {
    wireSwatchNav(card);
    if (card.dataset.vdSwatchWired) return;
    card.dataset.vdSwatchWired = '1';

    var primaryImg   = card.querySelector('.col-card__img--primary');
    var hoverImg     = card.querySelector('.col-card__img--hover');
    var cardLink     = card.querySelector('.col-card__link');
    var quickviewBtn = card.querySelector('.col-card__quickview-btn');
    var quickAddBtn  = card.querySelector('.col-card__quick-add-btn');
    var titleLink    = card.querySelector('.col-card__title a');
    var priceRow     = card.querySelector('.col-card__price-row');
    var imageWrap    = card.querySelector('.col-card__image-wrap');
    if (!primaryImg) return;

    var initialSaleEl = priceRow ? priceRow.querySelector('.col-card__price--sale, .col-card__price') : null;
    var initialCompareEl = priceRow ? priceRow.querySelector('.col-card__price--compare') : null;
    var current = {
      src: primaryImg.src,
      hoverSrc: hoverImg ? hoverImg.src : null,
      url: cardLink ? cardLink.href : null,
      title: titleLink ? titleLink.textContent : null,
      price: initialSaleEl ? parseInt(initialSaleEl.getAttribute('data-money'), 10) : NaN,
      compare: initialCompareEl ? parseInt(initialCompareEl.getAttribute('data-money'), 10) : NaN,
      discount: ''
    };

    function applyImage(src, fadeIn) {
      if (!src) return;
      primaryImg.style.opacity = '0';
      setTimeout(function() {
        // The primary card image has a srcset (responsive product-grid
        // images); with srcset still present, browsers keep resolving the
        // displayed image from it and silently ignore a plain .src change.
        // Must drop srcset first or swatch hover/click never actually
        // swaps the visible image.
        primaryImg.removeAttribute('srcset');
        primaryImg.src = src;
        primaryImg.style.opacity = '1';
        // Hand opacity control back to CSS so the main-image hover swap
        // (.col-card__image-wrap:hover) still works after a swatch click —
        // an inline style would otherwise permanently outrank that rule.
        setTimeout(function() { primaryImg.style.opacity = ''; }, 200);
      }, fadeIn ? 160 : 0);
    }

    function renderPriceRow(price, compare, discount) {
      if (!priceRow) return;
      priceRow.innerHTML = '';
      if (compare) {
        var sale = document.createElement('span');
        sale.className = 'col-card__price col-card__price--sale';
        sale.setAttribute('data-money', price);
        sale.textContent = formatMoney(price);
        var was = document.createElement('span');
        was.className = 'col-card__price col-card__price--compare';
        was.setAttribute('data-money', compare);
        was.textContent = formatMoney(compare);
        priceRow.appendChild(sale);
        priceRow.appendChild(was);
        if (discount) {
          var off = document.createElement('span');
          off.className = 'col-card__discount';
          off.textContent = discount + '% off';
          priceRow.appendChild(off);
        }
      } else {
        var span = document.createElement('span');
        span.className = 'col-card__price';
        span.setAttribute('data-money', price);
        span.textContent = formatMoney(price);
        priceRow.appendChild(span);
      }
    }

    function updateBadge(compare) {
      if (!imageWrap) return;
      var badge = imageWrap.querySelector('.col-card__badge.sale');
      if (compare) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'col-card__badge sale';
          badge.textContent = 'SALE';
          imageWrap.insertBefore(badge, imageWrap.firstChild);
        }
      } else if (badge) {
        badge.remove();
      }
    }

    var quickAddDefaultLabel = quickAddBtn ? quickAddBtn.textContent : '';

    card.querySelectorAll('.col-card__img-swatch').forEach(function(swatch) {
      var variantImg      = swatch.getAttribute('data-image');
      var variantHoverImg = swatch.getAttribute('data-hover-image');
      var variantUrl      = swatch.getAttribute('data-url');
      var variantTitle    = swatch.getAttribute('data-title');
      var variantPrice    = parseInt(swatch.getAttribute('data-price'), 10);
      var variantCompare  = parseInt(swatch.getAttribute('data-compare'), 10);
      var variantDiscount = swatch.getAttribute('data-discount');
      var variantId       = swatch.getAttribute('data-variant-id');
      var variantAvailable = swatch.getAttribute('data-available') === 'true';

      swatch.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        if (swatch.classList.contains('active')) return;

        card.querySelectorAll('.col-card__img-swatch').forEach(function(s) { s.classList.remove('active'); });
        swatch.classList.add('active');
        swatch.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });

        current = {
          src: variantImg || current.src,
          hoverSrc: variantHoverImg || current.hoverSrc,
          url: variantUrl || current.url,
          title: variantTitle || current.title,
          price: !isNaN(variantPrice) ? variantPrice : current.price,
          compare: !isNaN(variantCompare) ? variantCompare : NaN,
          discount: variantDiscount || ''
        };

        // Derive handle from data-handle attribute on swatch, or extract from URL
        var selectedHandle = swatch.getAttribute('data-handle') || (variantUrl ? variantUrl.split('/products/')[1] : null);

        applyImage(current.src, true);
        if (hoverImg && current.hoverSrc) {
          // Same srcset issue as applyImage() above.
          hoverImg.removeAttribute('srcset');
          hoverImg.src = current.hoverSrc;
        }
        if (cardLink && current.url) cardLink.href = current.url;
        // Update Quick View button handle to the selected sibling
        if (quickviewBtn && selectedHandle) quickviewBtn.setAttribute('data-quickview-handle', selectedHandle);
        // Keep "Add to Cart" pointed at the now-visible colour's variant -
        // otherwise it would silently add whichever colour the card loaded
        // with, regardless of which swatch the customer picked.
        if (quickAddBtn && variantId) {
          quickAddBtn.setAttribute('data-quick-add-variant-id', variantId);
          quickAddBtn.disabled = !variantAvailable;
          quickAddBtn.textContent = variantAvailable ? quickAddDefaultLabel : 'SOLD OUT';
        }
        if (titleLink && current.url) {
          titleLink.href = current.url;
          if (current.title) titleLink.textContent = current.title;
        }
        renderPriceRow(current.price, current.compare, current.discount);
        updateBadge(current.compare);
      });
    });
  }

  window.VDCollectionSwatches = { wireCard: wireCardSwatches };

  function wireAllCards() {
    document.querySelectorAll('.col-card').forEach(wireCardSwatches);
  }

  function hydrateCards() {
    var cards = document.querySelectorAll('.col-card[data-sibling-group]');
    if (!cards.length) return;

    var unrenderedCards = Array.prototype.slice.call(cards).filter(function (card) {
      var container = card.querySelector('.col-card__swatches');
      return container && !container.querySelectorAll('.col-card__img-swatch').length;
    });

    if (!unrenderedCards.length) {
      // All cards rendered server-side in Liquid! Wire interactivity directly.
      cards.forEach(function (card) {
        if (window.VDCollectionSwatches && window.VDCollectionSwatches.wireCard) {
          window.VDCollectionSwatches.wireCard(card);
        }
      });
      return;
    }

    buildIndex().then(function (map) {
      cards.forEach(function (card) {
        var container = card.querySelector('.col-card__swatches');
        if (!container) return;

        var sg = card.getAttribute('data-sibling-group');
        var handle = card.getAttribute('data-product-handle');
        var group = map[sg];

        // No group (feed failed) or single colour: clear the skeleton
        // placeholders so they don't shimmer forever.
        if (!group || group.length < 2) { container.innerHTML = ''; return; }

        // Render EVERY colour; the row scrolls horizontally.
        var existing = container.querySelectorAll('.col-card__img-swatch').length;
        if (existing >= group.length) return;

        var selfEntry = group.filter(function (p) { return p.handle === handle; })[0];
        var siblings = group.filter(function (p) { return p.handle !== handle; });
        if (!siblings.length) return;

        container.innerHTML = '';
        if (selfEntry) container.appendChild(buildSwatch(selfEntry, true));
        siblings.forEach(function (p) { container.appendChild(buildSwatch(p, false)); });

        if (window.VDCollectionSwatches && window.VDCollectionSwatches.wireCard) {
          window.VDCollectionSwatches.wireCard(card);
        }
      });
    });
  }

  // ── Product detail page: colour swatch mini-cards (image + name + price) ──
  function buildPdpSwatch(entry, isActive) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'col-card__img-swatch pdp-color-swatch' + (isActive ? ' active' : ' pdp-sibling-swatch');
    btn.setAttribute('data-url', entry.url);
    btn.setAttribute('aria-label', entry.title);
    var priceHtml = entry.price != null ? '<span class="pdp-color-swatch__price" data-money="' + entry.price + '">' + formatMoney(entry.price) + '</span>' : '';
    var swatchName = escapeHTML(entry.colorName || entry.title);
    btn.innerHTML =
      '<span class="pdp-color-swatch__img"><img src="' + entry.image + '" alt="' + swatchName + '" loading="lazy" width="60" height="74"></span>' +
      '<span class="pdp-color-swatch__name">' + swatchName + '</span>' +
      priceHtml;
    if (!isActive) {
      btn.addEventListener('click', function () {
        if (entry.url) window.location.href = entry.url;
      });
    }
    return btn;
  }

  var PDP_VISIBLE = 7;

  // Show only the first PDP_VISIBLE colours; add a Show more / Show less toggle
  // when there are more.
  function applyPdpCollapse(container) {
    var swatches = Array.prototype.slice.call(container.querySelectorAll('.col-card__img-swatch'));
    var parent = container.parentNode;
    var oldToggle = parent.querySelector('.pdp-color-toggle');
    if (oldToggle) oldToggle.remove();

    swatches.forEach(function (el, idx) {
      el.classList.toggle('pdp-color-swatch--extra', idx >= PDP_VISIBLE);
    });

    if (swatches.length <= PDP_VISIBLE) {
      container.classList.remove('pdp-color-swatches--collapsed');
      return;
    }

    container.classList.add('pdp-color-swatches--collapsed');
    var remaining = swatches.length - PDP_VISIBLE;
    var toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'pdp-color-toggle';
    toggle.textContent = 'Show more (' + remaining + ')';
    toggle.addEventListener('click', function () {
      var collapsed = container.classList.toggle('pdp-color-swatches--collapsed');
      toggle.textContent = collapsed ? 'Show more (' + remaining + ')' : 'Show less';
    });
    parent.insertBefore(toggle, container.nextSibling);
  }

  function hydratePdp() {
    var container = document.querySelector('.pdp-color-swatches[data-sibling-group]');
    if (!container) return;

    if (container.classList.contains('pdp-color-swatches--serverside')) {
      applyPdpCollapse(container);
      return;
    }

    var sg = container.getAttribute('data-sibling-group');
    var handle = container.getAttribute('data-product-handle');

    buildIndex().then(function (map) {
      var group = map[sg];
      if (group && group.length >= 2) {
        var existing = container.querySelectorAll('.col-card__img-swatch').length;
        if (existing < group.length) {
          // Stable catalog order; mark the current product active in place.
          container.innerHTML = '';
          group.forEach(function (entry) {
            container.appendChild(buildPdpSwatch(entry, entry.handle === handle));
          });
        }
      }
      applyPdpCollapse(container);
    });
  }

  var WISHLIST_ICON =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">' +
      '<path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>' +
    '</svg>';

  // Upsize a feed image URL (200px) for the larger card image.
  function bigImage(url) {
    return url ? url.replace(/([?&])width=\d+/, '$1width=600') : url;
  }

  // Build a full collection card (DOM) for a design shown in a chosen colour.
  function buildFilteredCard(sg, entry, group) {
    var card = document.createElement('div');
    card.className = 'col-card';
    card.setAttribute('data-sibling-group', sg);
    card.setAttribute('data-product-handle', entry.handle);
    card.innerHTML =
      '<div class="col-card__image-wrap">' +
        '<a href="' + entry.url + '" class="col-card__link" tabindex="-1" aria-hidden="true"></a>' +
        '<img src="' + bigImage(entry.image) + '" alt="' + escapeHTML(entry.title) + '" class="col-card__img col-card__img--primary" loading="lazy" width="400" height="500">' +
        '<button type="button" class="col-card__wishlist" data-wishlist-handle="' + entry.handle + '" aria-label="Add to Wishlist">' + WISHLIST_ICON + '</button>' +
        '<div class="col-card__cta">' +
          '<button type="button" class="col-card__cta-btn col-card__quickview-btn" data-quickview-handle="' + entry.handle + '">QUICK VIEW</button>' +
          '<a href="' + entry.url + '" class="col-card__cta-btn">VIEW DETAILS</a>' +
        '</div>' +
      '</div>' +
      '<div class="col-card__info">' +
        '<div class="col-card__swatches col-card__swatches--img"></div>' +
        '<h3 class="col-card__title"><a href="' + entry.url + '">' + escapeHTML(entry.title) + '</a></h3>' +
        '<div class="col-card__price-row"><span class="col-card__price" data-money="' + entry.price + '">' + formatMoney(entry.price) + '</span></div>' +
      '</div>';

    var swContainer = card.querySelector('.col-card__swatches');
    group.forEach(function (p) {
      swContainer.appendChild(buildSwatch(p, p.handle === entry.handle));
    });
    if (window.VDCollectionSwatches && window.VDCollectionSwatches.wireCard) {
      window.VDCollectionSwatches.wireCard(card);
    }
    return card;
  }

  function run() {
    wireAllCards();
    hydrateCards();
    hydratePdp();
  }

  // Exposed so infinite-scroll (collection.liquid) can hydrate appended cards.
  window.VDSiblingSwatches = { hydrate: hydrateCards };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
